<script>
  import { onMount } from "svelte";

  import {
    Button,
    CardLayout,
    NotificationModal,
    PageHead,
    PageLoading,
    RichTextEditor,
  } from "../../Components/Common/index.svelte";

  import { apiMethods } from "../../Services/index.svelte";

  import { user, isMobile, allGroups } from "../../stores";

  import {
    goBack,
    getText,
    getFormatDate,
    showError,
    goToURL,
    replaceURL,
    historyBack,
    isObjectEmpty,
    getBoolean,
    parseTemplateTypeName,
  } from "../../Utils/utilFunctions.svelte";

  import { deleteIcon, editIcon } from "../../Utils/utilConstants.svelte";

  import { cacheAllGroups } from "../../Utils/utilCache.svelte";

  import Select from "svelte-select/Select.svelte";

  import { DirectUserRoles } from "../../Utils/utilEnums.svelte";
  import {
    searchDoctorsFromCache,
    searchGroupsFromCache,
  } from "../../Utils/DataLoaders.svelte";

  export let params = {};

  $: if (params.id && templateDetails.id) isLoading = false;

  $: isEnableEditTemplate =
    isUserAdmin || isUserManager || isUserSupport || isUserDoctor;

  $: isUserAdmin = $user.role == DirectUserRoles.Admin;

  $: isUserManager = $user.role == DirectUserRoles.Manager;

  $: isUserSupport = $user.role == DirectUserRoles.Support;

  $: isUserDoctor = $user.role == DirectUserRoles.Doctor;

  $: if (isEditTemplate) btnOptions.map((btn) => (btn.isHidden = true));

  $: personaliseConditionalFailed = isEditTemplate
    ? isPersonaliseToGroup
      ? isObjectEmpty(selectedGroup) || !selectedGroup.group
      : isPersonaliseToDoctor
        ? isObjectEmpty(selectedDoctor) || !selectedDoctor.doctor
        : false
    : false;

  $: btnOptions = [
    {
      iconName: "deleteIcon",
      hasTooltip: false,
      iFont: deleteIcon,
      btnText: "Reset",
      iconFunction: () => _openDeleteTemplateModal(),
      isHidden: !templateDetails.groupId && !templateDetails.accountId,
    },
    {
      iconName: "editIconForManager",
      hasTooltip: false,
      iFont: editIcon,
      btnText: "Personalise to Group",
      iconFunction: () => {
        toggleEditTemplate();
        isPersonaliseToGroup = true;
      },
      isHidden:
        templateDetails.groupId || templateDetails.accountId
          ? true
          : isUserAdmin || isUserSupport
            ? !templateDetails.groupCustomisations
            : true,
    },
    {
      iconName: "editIconForDoctor",
      hasTooltip: false,
      iFont: editIcon,
      btnText: "Personalise to Doctor",
      iconFunction: () => {
        toggleEditTemplate();
        isPersonaliseToDoctor = true;
      },
      isHidden:
        templateDetails.groupId || templateDetails.accountId
          ? !(isUserManager && templateDetails.accountCustomisations)
          : isUserAdmin || isUserSupport
            ? !templateDetails.accountCustomisations
            : true,
    },
    {
      iconName: "editIcon",
      hasTooltip: false,
      iFont: editIcon,
      btnText:
        templateDetails.groupId || templateDetails.accountId || isUserAdmin
          ? "Edit"
          : "Personalise",
      isHidden: isEnableEditTemplate
        ? templateDetails.groupId || templateDetails.accountId
          ? isEditTemplate
          : isUserAdmin
            ? false
            : hasValidTemplateUnderGroup
        : true,
      iconFunction: () =>
        isUserAdmin
          ? toggleEditTemplate()
          : isUserDoctor
            ? redirectToTargetTemplatePage()
            : _openConfirmPersonaliseModal(),
    },
  ];

  let isLoading = false,
    templateDetails = {},
    defaultTemplateDetails = {},
    isEditTemplate = false,
    isCancelEditTemplateModalOpen = false,
    isUpdatingTemplate = false;

  let isConfirmPersonaliseModalOpen = false,
    isCheckingValidTemplates = false,
    hasValidTemplateUnderGroup = false,
    targetTemplate = {};

  let isDeleteTemplateModalOpen = false;

  let isPersonaliseToGroup = false,
    selectedGroup = {};

  let isPersonaliseToDoctor = false,
    selectedDoctor = {};

  onMount(async () => {
    if (params.id) await getTemplateDetails(params.id);

    await syncGroupToTemplate();

    _checkIfValidTemplateExists();
  });

  const handleHistoryBack = async () => {
    isEditTemplate = false;

    historyBack();

    setTimeout(async () => {
      const withinRoute = window.location.href.includes("EmailTemplates/view");

      if (!withinRoute) return;

      if (params.id) await getTemplateDetails(params.id);

      await syncGroupToTemplate();

      _checkIfValidTemplateExists();
    }, 10);
  };

  const getTemplateDetails = async (id) => {
    isLoading = true;

    await apiMethods.getMethod.getEmailTemplateById(id).then((res) => {
      if (res.id) _handleTemplateDetails(res);
      else
        showError(
          "Unable to load this invoice, please refresh the page and try again and contact support if the issue persists.",
          goBack,
        );
    });

    isLoading = false;
  };

  const syncGroupToTemplate = async () => {
    if (
      !isUserAdmin &&
      templateDetails.groupId &&
      templateDetails.groupId == $user.groupId
    )
      templateDetails.groupName = $user.groupName;
    else if (isUserAdmin) {
      await cacheAllGroups();

      if (!$allGroups.length) return;

      const targetGroup = $allGroups.find(
        (g) => g.id == templateDetails.groupId,
      );

      if (!targetGroup) return;

      templateDetails.groupName = targetGroup.name;
    }
  };

  const handleSaveTemplate = async () => {
    isUpdatingTemplate = true;

    // templateDetails.content = templateDetails.content.replace(/[\r\n]/g, "");

    if (!isPersonaliseToGroup && !isPersonaliseToDoctor) await updateTemplate();
    else if (isPersonaliseToGroup) await personaliseTemplateToGroup();
    else if (isPersonaliseToDoctor) await personaliseTemplateToDoctor();

    isUpdatingTemplate = false;
    toggleEditTemplate();
  };

  const updateTemplate = async () => {
    await apiMethods.putMethod
      .updateEmailTemplate(templateDetails)
      .then((res) => {
        if (!res || res.error)
          showError(
            res.error ??
              "Failed to update template details, please refresh the page and try again and contact support if the issue persists.",
          );
        else _handleTemplateDetails(res);
      });
  };

  const personaliseTemplateToGroup = async () => {
    const {
      group: { id },
    } = selectedGroup;

    await apiMethods.postMethod
      .createEmailTemplate({
        ...templateDetails,
        groupId: id,
      })
      .then((res) => {
        if (!res || res.error)
          showError(
            res.error ??
              "Failed to personalise template details, please refresh the page and try again and contact support if the issue persists.",
          );
        else if (res.id) {
          replaceURL(`/EmailTemplates/view/${res.id}`);
          _handleTemplateDetails(res);
          _checkIfValidTemplateExists();
        }
      });
  };

  const personaliseTemplateToDoctor = async () => {
    const {
      doctor: { id, groupId },
    } = selectedDoctor;

    await apiMethods.postMethod
      .createEmailTemplate({
        ...templateDetails,
        accountId: id,
        groupId,
      })
      .then((res) => {
        if (!res || res.error)
          showError(
            res.error ??
              "Failed to personalise template details, please refresh the page and try again and contact support if the issue persists.",
          );
        else if (res.id) {
          replaceURL(`/EmailTemplates/view/${res.id}`);
          _handleTemplateDetails(res);
          _checkIfValidTemplateExists();
        }
      });
  };

  const cancelEditTemplate = () => {
    templateDetails = { ...defaultTemplateDetails };
    toggleEditTemplate();
    _closeCancelEditTemplateModal();
    syncGroupToTemplate();
    isPersonaliseToGroup = false;
    selectedGroup = {};
    isPersonaliseToDoctor = false;
    selectedDoctor = {};
  };

  const saveEdit = async () => {
    await handleSaveTemplate();

    toggleEditTemplate();

    _closeCancelEditTemplateModal();
  };

  const resetTemplate = async () => {
    cancelEditTemplate();

    await apiMethods.deleteMethod
      .deleteEmailTemplate(templateDetails.id)
      .then((res) => {
        if (res.error) showError(res.error);
        else goBack();
      });
  };

  const redirectToTargetTemplatePage = async () => {
    _closeConfirmPersonaliseModal();

    if (!$user.groupId)
      await apiMethods.getMethod.getUserById($user.id).then((res) => {
        $user.groupId = res.groupId;
        $user.groupName = res.groupName;
      });

    if (hasValidTemplateUnderGroup) {
      goToURL(`/EmailTemplates/view/${targetTemplate.id}`);
      await getTemplateDetails(targetTemplate.id);
      await syncGroupToTemplate();
      _checkIfValidTemplateExists();
      toggleEditTemplate();
    } else {
      const obj = {
        ...templateDetails,
        groupId: $user.groupId,
      };

      if (isUserDoctor)
        await apiMethods.getMethod
          .getAccountByUserId($user.id)
          .then((res) => (obj.accountId = res.id));

      await apiMethods.postMethod.createEmailTemplate(obj).then(async (res) => {
        if (res.id) {
          replaceURL(`/EmailTemplates/view/${res.id}`);
          _handleTemplateDetails(res);
          _checkIfValidTemplateExists();
          toggleEditTemplate();
        }
      });
    }
  };

  const toggleEditTemplate = () => (isEditTemplate = !isEditTemplate);

  const clearSelectedGroup = () => (selectedGroup = {});

  const clearSelectedDoctor = () => (selectedDoctor = {});

  // HELPER FUNCTIONS
  const _handleTemplateDetails = (res) => {
    templateDetails = { ...res };
    defaultTemplateDetails = { ...res };

    syncGroupToTemplate();
  };

  const _checkIfValidTemplateExists = async () => {
    if (isUserAdmin) return;

    isCheckingValidTemplates = true;

    await apiMethods.getMethod
      .getEmailTemplates(0, 999999, {
        "filter[groupId]": $user.groupId,
      })
      .then((res) => {
        const { results } = res;

        targetTemplate = results
          .filter((res) => res.accountId || res.groupId)
          .find(
            (template) =>
              template.groupId == $user.groupId &&
              template.type == templateDetails.type &&
              template.name == templateDetails.name,
          );

        hasValidTemplateUnderGroup = targetTemplate ? true : false;
      });

    isCheckingValidTemplates = false;
  };

  const _openCancelEditTemplateModal = () =>
    (isCancelEditTemplateModalOpen = true);

  const _closeCancelEditTemplateModal = () =>
    (isCancelEditTemplateModalOpen = false);

  const _openConfirmPersonaliseModal = async () =>
    (isConfirmPersonaliseModalOpen = true);

  const _closeConfirmPersonaliseModal = () =>
    (isConfirmPersonaliseModalOpen = false);

  const _openDeleteTemplateModal = () => (isDeleteTemplateModalOpen = true);

  const _closeDeleteTemplateModal = () => (isDeleteTemplateModalOpen = false);
</script>

{#if isLoading}
  <PageLoading />
{:else}
  <PageHead title="View Email Template Details" backFunc={handleHistoryBack} />

  {#if hasValidTemplateUnderGroup && params.id != targetTemplate.id}
    <div class="flex w-full gap-x-4 py-2 cursor-default">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        class="stroke-secondary shrink-0 w-6 h-6"
        ><path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        ></path></svg
      >
      <p class="text-lg">
        This template is already personalised, click <span
          class="font-bold underline cursor-pointer"
          on:click={redirectToTargetTemplatePage}>here to edit</span
        >.
      </p>
    </div>
  {/if}

  <CardLayout title="Email Template Details" {btnOptions}>
    <div class="flex flex-col gap-4 flex-1 px-2 py-4 w-full lg:flex-row">
      <div class="flex flex-col flex-1 gap-4 h-full">
        <div class="flex flex-row gap-x-2">
          <p class="title">Name:</p>
          <p class="content">
            {getText(templateDetails.name)}
          </p>
        </div>

        <div class="flex flex-row gap-x-2">
          <p class="title">Type:</p>
          <p class="content">
            {parseTemplateTypeName(templateDetails.typeName)}
          </p>
        </div>

        {#if isUserAdmin}
          <div class="flex flex-1 gap-x-4">
            <div class="flex flex-1 gap-x-2">
              <p class="title">Group ID:</p>
              <p class="content">
                {getText(templateDetails.groupId, "-")}
              </p>
            </div>

            <div class="flex flex-1 gap-x-2">
              <p class="title">Group Name:</p>
              <p class="content">
                {getText(templateDetails.groupName, "-")}
              </p>
            </div>
          </div>
        {/if}

        <div class="flex flex-1 gap-x-4">
          <div class="flex flex-1 gap-x-2">
            <p class="title">Doctor ID:</p>
            <p class="content">
              {getText(templateDetails.accountId, "-")}
            </p>
          </div>

          <div class="flex flex-1 gap-x-2">
            <p class="title">Doctor Name:</p>
            <p class="content">
              {getText(templateDetails.accountName, "-")}
            </p>
          </div>
        </div>
      </div>

      {#if !$isMobile}
        <div class="divider lg:divider-horizontal" />
      {/if}

      <div class="flex flex-col flex-1 gap-y-4 h-full">
        <div class="flex flex-row gap-x-2">
          <p class="title">Created Date:</p>
          <p class="content">
            {getFormatDate(templateDetails.createdDate)}
          </p>
        </div>

        <div class="flex flex-row gap-x-2">
          <p class="title">Updated Date:</p>
          <p class="content">
            {getFormatDate(templateDetails.updatedDate)}
          </p>
        </div>

        <div class="flex flex-row gap-x-2">
          <p class="title">Active:</p>
          {#if isEditTemplate && (isUserAdmin || isUserSupport || (isUserManager && templateDetails.groupId == $user.groupId) || (isUserDoctor && templateDetails.accountId))}
            <input
              type="checkbox"
              class="toggle toggle-secondary"
              bind:checked={templateDetails.active}
            />
          {:else}
            <p class="content">
              {getBoolean(templateDetails.active)}
            </p>
          {/if}
        </div>
      </div>
    </div>

    {#if isEditTemplate}
      {#if isPersonaliseToGroup}
        <div
          class="flex flex-1 items-center gap-x-2 p-2 {$isMobile
            ? 'flex-col'
            : ''}"
        >
          <p class="title">Select group:</p>

          <Select
            bind:value={selectedGroup.group}
            class="flex flex-1 rounded-full"
            loadOptions={searchGroupsFromCache}
            itemId="id"
            label="name"
            on:clear={clearSelectedGroup}
            noOptionsMessage={"No groups found"}
            --list-z-index="3"
            --height="32px"
          />
        </div>
      {:else if isPersonaliseToDoctor}
        <div
          class="flex flex-1 items-center gap-x-2 p-2 {$isMobile
            ? 'flex-col'
            : ''}"
        >
          <p class="title">Select doctor:</p>

          <Select
            bind:value={selectedDoctor.doctor}
            class="flex flex-1 max-h-[35px] rounded-full"
            loadOptions={searchDoctorsFromCache}
            itemId="id"
            label="displayName"
            on:clear={clearSelectedDoctor}
            noOptionsMessage={"No doctors found"}
            --list-z-index="3"
          />
        </div>
      {/if}
    {/if}

    <div class="flex flex-col flex-1 p-2 gap-y-4 h-full">
      <div
        class="flex flex-1 items-center gap-x-2 {$isMobile ? 'flex-col' : ''}"
      >
        <p class="title">Subject:</p>
        {#if isEditTemplate}
          <input
            type="text"
            class="input input-sm input-bordered flex-1"
            bind:value={templateDetails.subject}
          />
        {:else}
          <p class="content">
            {getText(templateDetails.subject)}
          </p>
        {/if}
      </div>

      <div class="flex gap-x-2 {$isMobile ? 'flex-col' : ''}">
        <p class="title">Content:</p>
        {#if isEditTemplate}
          <div class="content break-all">
            <RichTextEditor bind:content={templateDetails.content} />
          </div>
        {:else}
          <p
            class="content pr-3 whitespace-pre-wrap font-sans break-words break-all {$isMobile
              ? 'content-width'
              : ''}"
          >
            {@html templateDetails.content?.replace(/[\r\n]/g, "")}
          </p>
        {/if}
      </div>

      {#if isEditTemplate}
        <div class="flex flex-1 gap-x-4 items-center justify-end">
          <Button
            manualClass="min-w-[100px] btn-error"
            clickFunc={_openCancelEditTemplateModal}
            isDisabled={isUpdatingTemplate}
          >
            Cancel
          </Button>
          <Button
            manualClass={`min-w-[100px] ${isUpdatingTemplate ? "loading" : ""}`}
            clickFunc={handleSaveTemplate}
            isDisabled={isUpdatingTemplate || personaliseConditionalFailed}
          >
            {isUpdatingTemplate ? "Saving" : "Save"}
          </Button>
        </div>
      {/if}
    </div>
  </CardLayout>
{/if}

<NotificationModal
  isModalOpen={isConfirmPersonaliseModalOpen}
  title="Notice"
  cancelText="No"
  submitText="Yes"
  closeModal={_closeConfirmPersonaliseModal}
  confirmAction={redirectToTargetTemplatePage}
  closeModalFunction={_closeConfirmPersonaliseModal}
>
  Do you want to personalise this template
</NotificationModal>

<NotificationModal
  bind:isModalOpen={isCancelEditTemplateModalOpen}
  title="Cancel edit"
  cancelText="Discard changes"
  closeModal={cancelEditTemplate}
  showConfirmBtn={false}
  isConfirming={isUpdatingTemplate}
  hideCrossButton
  preventESCClose
  closeModalFunction={cancelEditTemplate}
  btnOptions={[
    {
      text: "Save and Continue",
      loading: personaliseConditionalFailed,
      function: () => saveEdit(),
    },
  ]}
>
  Discard all changes
</NotificationModal>

<NotificationModal
  isModalOpen={isDeleteTemplateModalOpen}
  title="Reset Template"
  isReverseBtns
  cancelText="Reset"
  submitText="Cancel"
  confirmButtonClass="btn-accent"
  closeModal={resetTemplate}
  confirmAction={_closeDeleteTemplateModal}
  closeModalFunction={_closeDeleteTemplateModal}
>
  Remove this template customisation?
</NotificationModal>

<style>
  .title {
    width: 120px;
  }

  .content {
    width: calc(100% - 120px);
  }

  .content-width {
    width: calc(100vw - 120px);
  }
</style>
